import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch, batch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import TableToExcel from '@linways/table-to-excel';
import download from 'downloadjs';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../shared/_TBComponents/Button';
import Modal from '../../../shared/_TBComponents/Modal';
import Text from '../../../shared/_TBComponents/Text';

import AddAthleteModal from './presentational/AddAthleteModal';
import AddOptionsModal from './presentational/AddOptionsModal';
import UploadUsersModal from './presentational/UploadUsersModal';
import FilterAthletesModal from './presentational/FilterAthletesModal';
import ManageAthletesTable from './presentational/ManageAthletesTable';
import ManageAthletesBulkActions from './presentational/ManageAthletesBulkActions';
import ManageAthletesModals from './presentational/ManageAthletesModals';
import ModalSupportNew from '../../../shared/components/ModalSupportNew';
import {
  fetchArchivedAthletes, fetchAthletes, fetchPrograms,
} from '../ducks/navigatorActions';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #F5F5F5;
  overflow: hidden;
`;

const TopSection = styled.div`
  background: #FFF;
  padding: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

const BottomSection = styled.div`
  flex: 1;
  min-height: 0;
  background: #FFF;
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0 24px 24px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 100%;
`;

const ExportBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HiddenTable = styled.table`
  display: none;
`;

const EMPTY_FILTERS = {
  filterMode: 'any',
  filterGroups: [],
  filterCalendars: [],
  filterGenders: [],
};

const DEFAULT_COLUMN_VISIBILITY = {
  userId: false,
  externalId: false,
  ssoId: false,
  smartabaseId: false,
  lastActivity: false,
  joinedDate: false,
};

const ManageAthletes = ({ backPath, onEditAthlete }) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const accountCode = useSelector((state) => state.auth.data.currentUser?.accountCode);
  const accountsList = useSelector((state) => state.auth.data.currentUser?.accountsList);
  const athletes = useSelector((state) => state.navigator.data.athletes);
  const archivedAthletes = useSelector((state) => state.navigator.data.archivedAthletes);
  const calendars = useSelector((state) => state.navigator.data.calendars);
  const groups = useSelector((state) => state.navigator.data.groups);
  const programs = useSelector((state) => state.navigator.data.programs);
  const archivedUserSlots = useSelector(
    (state) => state.auth.data.organizationSettings?.organization?.archivedUserSlots ?? null,
  );

  const [athleteToDelete, setAthleteToDelete] = useState(null);
  const [athleteToResend, setAthleteToResend] = useState(null);
  const [athleteToArchive, setAthleteToArchive] = useState(null);
  const [athleteToRestore, setAthleteToRestore] = useState(null);
  const [isArchived, setIsArchived] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [isAddAthleteModalOpen, setIsAddAthleteModalOpen] = useState(false);
  const [isAddOptionsModalOpen, setIsAddOptionsModalOpen] = useState(false);
  const [isUploadUsersModalOpen, setIsUploadUsersModalOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState(EMPTY_FILTERS);
  const [columnVisibility, setColumnVisibility] = useState(DEFAULT_COLUMN_VISIBILITY);

  const hasActiveFilters = (
    activeFilters.filterGroups.length > 0
    || activeFilters.filterCalendars.length > 0
    || activeFilters.filterGenders.length > 0
  );

  useEffect(() => {
    if (accountCode) {
      batch(() => {
        dispatch(fetchAthletes(accountCode));
        dispatch(fetchPrograms(accountCode));
      });
    }
  }, [accountCode, dispatch]);

  useEffect(() => {
    if (location.state?.openUploadModal) {
      setIsUploadUsersModalOpen(true);
      history.replace(location.pathname, { ...location.state, openUploadModal: false });
    }
  }, [location.state?.openUploadModal]);

  const refetchAthletes = () => {
    dispatch(fetchAthletes(accountCode, activeFilters));
    if (isArchived) {
      dispatch(fetchArchivedAthletes(accountCode));
    }
  };

  const refetchArchivedAthletes = () => {
    dispatch(fetchArchivedAthletes(accountCode));
  };

  const handleApplyFilters = ({ columnVisibility: newColVis, ...filters }) => {
    setActiveFilters(filters);
    setColumnVisibility(newColVis);
    setIsFilterOpen(false);
    dispatch(fetchAthletes(accountCode, filters));
  };

  const handleResetFilters = () => {
    setActiveFilters(EMPTY_FILTERS);
    setColumnVisibility(DEFAULT_COLUMN_VISIBILITY);
    setIsFilterOpen(false);
    dispatch(fetchAthletes(accountCode));
  };

  const calendarMap = useMemo(
    () => calendars.reduce((acc, cal) => { acc[cal.id] = cal; return acc; }, {}),
    [calendars],
  );

  const getExportFileName = (ext) => {
    const date = new Date().toISOString().split('T')[0];
    return `athletes_export_${date}.${ext}`;
  };

  const exportToXLSX = () => {
    const table = document.getElementById('athletes-export-table');
    TableToExcel.convert(table, {
      name: getExportFileName('xlsx'),
      sheet: { name: 'Athletes' },
    });
  };

  const exportToXLS = () => {
    const table = document.getElementById('athletes-export-table');
    TableToExcel.convert(table, {
      name: getExportFileName('xls'),
      sheet: { name: 'Athletes' },
    });
  };

  const exportToCSV = () => {
    const headers = ['First', 'Last', 'Email', 'Groups', 'Calendar', 'Status'];
    const source = isArchived ? archivedAthletes : athletes;
    const rows = source.map((a) => [
      a.first || '',
      a.last || '',
      a.email || '',
      (a.groupAssignments || []).map((g) => g.name).join('; '),
      calendarMap[a.calendarAssignment?.calendarId]?.name || '',
      a.activated === 1 ? 'Registered' : 'Pending',
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    download(csv, getExportFileName('csv'), 'text/csv');
  };

  const addAthleteOptions = [
    {
      key: 'easy-join',
      title: 'Easy-Join',
      description: 'Generate a shareable link and allow athletes to create their own profile.',
      icon: 'Link',
      onClick: () => history.push('/easy-join', { backPath: '/dashboard/manage-athletes', backLabel: 'Back to Manage Athletes' }),
    },
    {
      key: 'import-bulk',
      title: 'Import in Bulk',
      description: 'Create multiple athlete profiles using a CSV file.',
      icon: 'Users',
      onClick: () => {
        setIsAddOptionsModalOpen(false);
        setIsUploadUsersModalOpen(true);
      },
    },
    {
      key: 'one-at-a-time',
      title: 'Individual Athlete',
      description: 'Create a single athlete profile.',
      icon: 'add-user',
      onClick: () => {
        setIsAddOptionsModalOpen(false);
        setIsAddAthleteModalOpen(true);
      },
    },
  ];

  return (
    <Container>
      <TopSection>
        <TitleGroup>
          <Button
            name='Back to Home'
            icon='left-arrow'
            iconSize={8}
            iconColor='#9E9E9E'
            bgColor='transparent'
            noBorder
            txtColor='#9E9E9E'
            customBorderRadius='4px'
            styledTextProps={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.3px' }}
            styledWrapperProps={{ paddingLeft: '0' }}
            onClick={() => history.push(backPath)}
          />
          <Text styledTextProps={{ fontSize: '24px', fontWeight: 900 }}>
            Manage Athletes
          </Text>
        </TitleGroup>
        <ActionButtonGroup>
          <Button
            name='Add Athletes'
            icon='add-user'
            iconColor='#444444'
            bgColor='#FFF'
            borderColor='#EEE'
            customBorderRadius='4px'
            styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
            styledWrapperProps={{ boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)' }}
            onClick={() => setIsAddOptionsModalOpen(true)}
          />
          <Button
            name='Export'
            icon='download'
            iconColor='#FFF'
            customBorderRadius='4px'
            borderColor='#444444'
            txtColor='#FFF'
            bgColor='#444444'
            styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
            onClick={() => setIsExportOpen(true)}
          />
        </ActionButtonGroup>
      </TopSection>

      <BottomSection>
        <ManageAthletesTable
          onEditAthlete={onEditAthlete}
          onDeleteAthlete={setAthleteToDelete}
          onResendActivation={setAthleteToResend}
          onArchiveAthlete={setAthleteToArchive}
          onRestoreAthlete={setAthleteToRestore}
          isArchived={isArchived}
          onToggleArchived={setIsArchived}
          archivedUserSlots={archivedUserSlots}
          onOpenSupport={() => setIsSupportOpen(true)}
          onBulkAction={(action, ids) => {
            setBulkAction(action);
            setBulkSelectedIds(ids);
          }}
          columnVisibility={columnVisibility}
          onOpenFilter={() => setIsFilterOpen(true)}
          hasActiveFilters={hasActiveFilters}
          activeFiltersCount={
            [activeFilters.filterGroups, activeFilters.filterCalendars, activeFilters.filterGenders]
              .filter((a) => a.length > 0).length
          }
        />
      </BottomSection>

      <Modal
        isOpen={isExportOpen}
        onRequestClose={() => setIsExportOpen(false)}
        customBorderRadius='8px'
        customPadding='24px'
        customMaxWidth='400px'
        customMinHeight='200px'
      >
        <ContentWrapper>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
            Export Data
          </Text>
          <Text styledWrapperProps={{ marginBottom: '24px' }}>
            Export the currently visible list by clicking a file type below.
          </Text>
          <ExportBody>
            <Button
              name='XLSX'
              fullWidth
              bgColor='#444444'
              borderColor='#444444'
              txtColor='#FFF'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={exportToXLSX}
            />
            <Button
              name='XLS'
              fullWidth
              bgColor='#444444'
              borderColor='#444444'
              txtColor='#FFF'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={exportToXLS}
            />
            <Button
              name='CSV'
              fullWidth
              bgColor='#444444'
              borderColor='#444444'
              txtColor='#FFF'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={exportToCSV}
            />
          </ExportBody>
        </ContentWrapper>
      </Modal>

      <AddOptionsModal
        isOpen={isAddOptionsModalOpen}
        onClose={() => setIsAddOptionsModalOpen(false)}
        options={addAthleteOptions}
      />

      <ManageAthletesModals
        accountCode={accountCode}
        athleteToDelete={athleteToDelete}
        athleteToResend={athleteToResend}
        athleteToArchive={athleteToArchive}
        athleteToRestore={athleteToRestore}
        onClearDelete={() => setAthleteToDelete(null)}
        onClearResend={() => setAthleteToResend(null)}
        onClearArchive={() => setAthleteToArchive(null)}
        onClearRestore={() => setAthleteToRestore(null)}
        onRefetch={refetchAthletes}
        onRefetchArchived={refetchArchivedAthletes}
      />

      <ManageAthletesBulkActions
        bulkAction={bulkAction}
        bulkSelectedIds={bulkSelectedIds}
        onClose={() => { setBulkAction(null); setBulkSelectedIds([]); }}
        onSetBulkAction={setBulkAction}
        accountCode={accountCode}
        accountsList={accountsList}
        groups={groups}
        calendars={calendars}
        programs={programs}
        onRefetch={refetchAthletes}
      />

      <FilterAthletesModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        groups={groups}
        calendars={calendars}
        activeFilters={activeFilters}
        columnVisibility={columnVisibility}
      />

      <AddAthleteModal
        isOpen={isAddAthleteModalOpen}
        onClose={() => setIsAddAthleteModalOpen(false)}
        onCreated={() => refetchAthletes()}
      />

      <UploadUsersModal
        isOpen={isUploadUsersModalOpen}
        onClose={() => setIsUploadUsersModalOpen(false)}
        onDone={() => {
          setIsUploadUsersModalOpen(false);
          refetchAthletes();
        }}
      />

      <ModalSupportNew
        isOpen={isSupportOpen}
        onRequestClose={() => setIsSupportOpen(false)}
        path='/dashboard/manage-athletes'
      />

      <HiddenTable id='athletes-export-table'>
        <thead>
          <tr>
            <th>First</th>
            <th>Last</th>
            <th>Email</th>
            <th>Groups</th>
            <th>Calendar</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {(isArchived ? archivedAthletes : athletes).map((a) => (
            <tr key={a.id}>
              <td>{a.first || ''}</td>
              <td>{a.last || ''}</td>
              <td>{a.email || ''}</td>
              <td>{(a.groupAssignments || []).map((g) => g.name).join(', ')}</td>
              <td>{calendarMap[a.calendarAssignment?.calendarId]?.name || ''}</td>
              <td>{a.activated === 1 ? 'Registered' : 'Pending'}</td>
            </tr>
          ))}
        </tbody>
      </HiddenTable>
    </Container>
  );
};

ManageAthletes.propTypes = {
  backPath: PropTypes.string,
  onEditAthlete: PropTypes.func,
};

ManageAthletes.defaultProps = {
  backPath: '/dashboard/athletes',
  onEditAthlete: () => {},
};

export default ManageAthletes;
